import { CompanyAccount } from '@/models/companyModels';
import { envVars } from '@/services/ApiService';
import { REDIS_URL } from '@/constants/env';

export const SKIP_HOST_FETCH = 'SKIP_HOST_FETCH';

const CACHE_TTL_SECONDS = 10 * 60;

let redisClient: any = null;

async function getRedisClient(): Promise<any> {
  if (!REDIS_URL || typeof window !== 'undefined') return null;
  if (redisClient?.isOpen) return redisClient;

  const { createClient } = await import('redis');
  redisClient = createClient({ url: REDIS_URL });
  redisClient.on('error', (err: Error) => console.error('[PortalCache] Redis error', err));
  await redisClient.connect();
  return redisClient;
}

async function getCached(key: string): Promise<CompanyAccount | null> {
  try {
    const client = await getRedisClient();
    if (!client) return null;
    const raw = await client.get(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function setCached(key: string, data: CompanyAccount): Promise<void> {
  try {
    const client = await getRedisClient();
    if (!client) return;
    await client.set(key, JSON.stringify(data), { EX: CACHE_TTL_SECONDS });
  } catch {
    // cache write failure is non-fatal
  }
}

const isLocalOrEmpty = (h?: string) => !h || h === 'localhost' || h.startsWith('localhost:');

export async function getPortalJsonByHost(host: string, req?: any): Promise<CompanyAccount> {
  if (isLocalOrEmpty(host)) throw new Error(SKIP_HOST_FETCH);

  const cached = await getCached(`portal_json:host:${host}`);
  if (cached) return cached;

  const headers: HeadersInit = {};
  if (req) {
    const forwardedFor = req.headers['x-forwarded-host'] || req.headers.host;
    if (forwardedFor) {
      const headerValue = Array.isArray(forwardedFor) ? forwardedFor[0] : forwardedFor;
      headers['X-Forwarded-For'] = headerValue;
    }
  }

  const resp = await fetch(`${envVars.ambassadorApiUri}/portal.json?host=${host}`, { headers });
  if (!resp.ok) throw new Error(resp.statusText);

  const data = await resp.json();
  const result: CompanyAccount = Array.isArray(data) ? data[0] : data;
  await setCached(`portal_json:host:${host}`, result);
  return result;
}

export async function getPortalJsonByUid(uid: number, req?: any): Promise<CompanyAccount> {
  const safeUid = encodeURIComponent(String(uid));
  const isServer = typeof window === 'undefined';

  if (isServer) {
    const cached = await getCached(`portal_json:uid:${uid}`);
    if (cached) return cached;

    const headers: HeadersInit = {};
    if (req) {
      const forwardedFor = req.headers['x-forwarded-host'] || req.headers.host;
      if (forwardedFor) {
        const headerValue = Array.isArray(forwardedFor) ? forwardedFor[0] : forwardedFor;
        headers['X-Forwarded-For'] = headerValue;
      }
    }

    const resp = await fetch(`${envVars.ambassadorApiUri}/portal.json?uid=${safeUid}`, { headers });
    if (!resp.ok) throw new Error(resp.statusText);

    const data = await resp.json();
    const result: CompanyAccount = Array.isArray(data) ? data[0] : data;
    await setCached(`portal_json:uid:${uid}`, result);
    return result;
  }

  const resp = await fetch(`/api/storefront/portalByUid?uid=${safeUid}`, {
    credentials: 'same-origin',
  });
  if (!resp.ok) throw new Error(resp.statusText);

  const data = await resp.json();
  return Array.isArray(data) ? data[0] : data;
}
